<template>
  <o-form-wrapper
    :form-state="formState"
    :schema="schema"
    :server-errors="_se"
    :v="$v.item"
    v-model="item"
  >
    <div class="row">
      <div class="col-10">
        <b-alert variant="danger" show v-if="errorMsg">{{ errorMsg }}</b-alert>
        <o-widget-wrapper
          :form-state="formState"
          :schema="schema"
          :server-errors="_se"
          :validators="$v.item"
          name="form_template_version"
        >
          <f-select-template
            :form-state="formState"
            @templateVersion="set_templateVersion"
            :validators="$v"
            :server-errors="_se"
            v-model="item.form_template_version"
          >
          </f-select-template>
        </o-widget-wrapper>
      </div>
      <div class="col-2">
        <qrcode :value="form_url" v-if="show_qr_code" />
      </div>
    </div>

    <span v-if="templateVersion">
      <o-widget-wrapper
        :schema="schema"
        :server-errors="_se"
        :validators="$v.item"
        name="data"
      >
        <f-template
          :design="false"
          :fields="templateVersion.fields"
          :form-state="formState"
          :form-context="formContext"
          :disabled="isLocked || lock_on_submit"
          :has-score="templateVersion.option_keep_score"
          v-bind:score.sync="item.score"
          v-model="item.data"
          @on-validation="on_validation"
          @error="handleError"
        ></f-template>
      </o-widget-wrapper>

      <o-widget-wrapper
        :form-state="formState"
        :schema="schema"
        :server-errors="_se"
        :validators="$v.item"
        name="tags"
        v-if="templateVersion.option_has_tags"
      >
        <o-tag-input
          :disabled="isLocked"
          :form-state="formState"
          :server-errors="_se"
          :validators="$v.item"
          name="tags"
          v-bind="schema.fields.tags"
          v-model="item.tags"
        ></o-tag-input>
      </o-widget-wrapper>

      <div v-if="templateVersion.option_must_sign_off">
        <o-widget-wrapper
          :form-state="formState"
          :schema="schema"
          :server-errors="_se"
          :validators="$v.item"
          name="sign_off_done"
        >
          <o-checkbox
            :form-state="formState"
            :label="templateVersion.option_sign_off_message"
            :server-errors="_se"
            :validators="$v.item"
            name="sign_off_done"
            v-bind="schema.fields.sign_off_done"
            v-model="item.sign_off_done"
          ></o-checkbox>
        </o-widget-wrapper>

        <div v-if="templateVersion.option_multiple_sign_offs">
          <table class="table table-bordered">
            <thead>
              <th>User</th>
              <th style="width: 50%" class="text-right">
                <o-list-add
                  :default-value="newParticipant"
                  item-name="Participant"
                  v-model="item.sign_offs"
                  v-if="!isSubmitted"
                ></o-list-add>
              </th>
            </thead>
            <tbody>
              <tr
                v-for="(signoff, index) in item.sign_offs"
                :key="index"
                :class="{ 'table-info': theSignOff === signoff.id }"
              >
                <td class="p-0">
                  <span v-if="signoff.email_sent">
                    <user-display
                      :value="signoff.participant"
                      :list="optionUserItemsById"
                    />
                  </span>
                  <o-select
                    v-if="!signoff.email_sent"
                    flat
                    clearable
                    v-model="signoff.participant"
                    :items="optionUserItems"
                    text-field="username"
                    nolabel
                    value-field="id"
                    :validator="$v.item.sign_offs.$each[index]['participant']"
                    :loading="optionUserWorking"
                    name="participant"
                    :validators="$v.item.sign_offs.$each[index]"
                    :server-errors="_se"
                    :server-errors-index="index"
                    :form-state="formState"
                  ></o-select>
                </td>
                <td class="p-0 text-right">
                  <o-list-actions
                    :list="item.sign_offs"
                    :index="index"
                    disable-move
                    :disable-delete="!!signoff.email_sent || isSubmitted"
                  >
                    <template v-slot:prefix>
                      <button
                        class="btn btn-success"
                        v-if="canConfirm(signoff)"
                        @click="confirmSignOff(signoff)"
                      >
                        Confirm
                      </button>
                      <button
                        class="btn btn-success"
                        v-if="signoff.date_confirmed != null"
                        :disabled="true"
                        @click="confirmSignOff(signoff)"
                      >
                        Confirmed
                      </button>
                      <button
                        class="btn btn-warning"
                        v-if="
                          !canConfirm(signoff) &&
                          !signoff.date_confirmed &&
                          signoff.pk
                        "
                        :disabled="true"
                        @click="confirmSignOff(signoff)"
                      >
                        Unconfirmed
                      </button>
                    </template>
                  </o-list-actions>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="templateVersion.option_form_is_submitted">
        <o-checkbox
          :disabled="!canSubmit"
          :form-state="formState"
          :server-errors="_se"
          :validators="$v.item"
          :value="isSubmitted"
          @input="submitThis"
          label="Submit"
          name="submitted"
        ></o-checkbox>

        <div v-if="isSubmitted" class="well">
          <date-display
            :value="item.submitted_date"
            format="dddd DD MMM YYYY hh:mm"
          ></date-display>
          by
          <user-display
            :value="item.submitted_user"
            :list="optionUserItemsById"
          ></user-display>
        </div>
      </div>
    </span>

    <trigger-buttons
      :workflow-instance="item"
      :current-user="currentUser"
      :do-save="_doSave"
      :item="item"
      @success="reload"
      :disabled-buttons="trigger_disabled_buttons"
    ></trigger-buttons>

    <!--o-do-server-action-button
      v-if="item.id"
      class="btn btn-primary"
      :uri="getUrl"
      :request="request"
      @success="reload"
      variant-color="primary"
    >
      Download PDF
    </o-do-server-action-button-->
  </o-form-wrapper>
</template>

<script>
import moment from "moment";
import axios from "axios";

import schema from "../../schema/index";
import { cleanUpModel } from "../../../../vue-ocom/schema/schema_vuetify.js";
import {
  CreateCodeTableFetchMixin,
  CreateFormStateMixin,
  CreateGroupPermissionMixin,
  VuelidateMixin,
} from "../../../../vue-ocom/mixins";
import { validationMixin } from "vuelidate";
import OWidgetWrapper from "../../../../vue-ocom/components/forms/OWidgetWrapper.vue";
import OTextField from "../../../../vue-ocom/components/forms/fields/OTextField.vue";
import OSelect from "../../../../vue-ocom/components/forms/fields/OSelect.vue";
import OCheckbox from "../../../../vue-ocom/components/forms/fields/OCheckbox.vue";
import OFormWrapper from "../../../../vue-ocom/components/forms/OFormWrapper.vue";
import ODateTime from "../../../../vue-ocom/components/forms/fields/ODateTime.vue";
import OTagInput from "@/vue-ocom/components/forms/fields/OTagInput.vue";

import FSelectTemplate from "../../components/FSelectTemplate.vue";
import FTemplate from "../../../FTemplate.vue";

import Qrcode from "@chenfengyuan/vue-qrcode/src";
import { mapActions, mapGetters } from "vuex";
import { generateGUID } from "../../../utils.js";
import { CreateDefaultItem } from "../../../../vue-ocom/schema/schema_utils.js";
import TriggerButtons from "../../../components/TriggerButtons.vue";
import { forEach } from "lodash";

export default {
  mixins: [
    VuelidateMixin,
    validationMixin,
    CreateFormStateMixin("$v"),
    CreateGroupPermissionMixin("FormEntry"),
    CreateCodeTableFetchMixin(["User", "FormTemplateVersion"]),
  ],
  components: {
    Qrcode,
    FSelectTemplate,
    FTemplate,
    OFormWrapper,
    OWidgetWrapper,
    ODateTime,
    OTagInput,

    OCheckbox,
    OSelect,
    OTextField,
    TriggerButtons,
  },
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    level: { type: Number, default: 0 },
    stateId: { type: [Number, String], default: "" },
    submitting: { type: Boolean, default: false },
    _doSave: { type: Function, default: null },
    currentState: { type: [String, Number], default: "" },
  },
  data: () => ({
    schemaModel: cleanUpModel(schema.FormEntry, {}),
    design: false, // always for here
    templateVersion: null,
    theSignOff: null,
    last_validation: null,
    errorMsg: "",
    workflowFields: {},
    stateFinished: false,
  }),
  methods: {
    ...mapActions("JobModule", { jobGetById: "getById" }),
    OnBeforeGetFormTemplateCodeList(list_options) {
      list_options.filter = "for_permissions";
    },
    on_validation(val) {
      this.last_validation = val;
      if (!this.templateVersion.option_form_is_submitted) {
        this.$emit("can-save", val);
      }
    },
    set_templateVersion(newTemplateVersion) {
      this.templateVersion = newTemplateVersion;

      this.$emit("templateSelected", newTemplateVersion);

      if (!this.item.uuid) {
        this.item.uuid = generateGUID();
      }

      // Get workflow field state for this template version
      if (newTemplateVersion && newTemplateVersion.id) {
        this.GetFieldState(newTemplateVersion.id, this.currentState);
      }
    },
    GetFieldState(template_version_id, currentState) {
      const vm = this;
      // Derive state id from current_user_states (same source as formContext.states);
      // fall back to the passed currentState or stateId prop for direct URL navigation.
      const userStates = vm.item.current_user_states || [];
      const resolvedState =
        !vm.item.id
          ? "start"
          : userStates.length > 0
          ? userStates[0].id
          : currentState || vm.stateId;
      const requestData = {
        template_version_id: template_version_id,
        data: vm.item.data,
        state: resolvedState,
        entry_id: vm.item.id,
      };

      axios
        .post("/forms247/v1/get_form_state/", requestData)
        .then((response) => {
          vm.workflowFields = response.data || {};

          vm.stateFinished = vm.workflowFields["__finishedState"] || false;
          vm.$emit("buttons-loaded", response.data.buttons || []);
        })
        .catch((error) => {
          console.error("Failed to get form state:", error);
          vm.workflowFields = {};
        });
    },
    signOffForMe(signOff, yes) {
      if (yes) {
        signOff.date_confirmed = this.item.submitted_date;
        signOff.email_sent = -1;
      } else {
        signOff.date_confirmed = null;
        signOff.email_sent = null;
      }
    },
    submitThis(value) {
      var found_signOff = _.filter(this.item.sign_offs, {
        participant: this.currentUser.id,
      }); // find my sign off

      if (value) {
        this.item.submitted_user = this.currentUser.id;
        this.item.submitted_date = new Date(); // now

        if (found_signOff) {
          this.signOffForMe(found_signOff, true);
        }

        this.$emit("update:submitting", true);
      } else {
        this.item.submitted_user = null;
        this.item.submitted_date = null;

        if (found_signOff) {
          this.signOffForMe(found_signOff, false);
        }
        this.$emit("update:submitting", false);
      }
    },

    /* Sign off Code */
    newParticipant() {
      const result = CreateDefaultItem(schema.FormEntrySignOff);
      result.uuid = generateGUID();

      if (this.item.sign_offs.length < 1) {
        result.participant = this.currentUser.id; // First One is ME

        if (this.item.submitted_user === this.currentUser.id) {
          this.signOffForMe(result, true);
        }
      }

      return result;
    },
    confirmSignOff(signOff) {
      this.$set(signOff, "date_confirmed", new Date());
    },
    canConfirm(signOff) {
      if (signOff.participant === this.currentUser.id) {
        return signOff.email_sent && signOff.date_confirmed == null;
      }
      return false;
    },
    reload(data) {
      this.$emit("reload");
    },
    handleError(msg) {
      this.errorMsg = msg;
    },
  },
  computed: {
    ...mapGetters("AuthModule", ["currentUser"]),
    _se() {
      // Return an empty Object if there are no Server Errors to avoid Binding Errors
      return this.serverErrors || {};
    },
    isLocked() {
      // Check if the current user can edit the data
      // Check item.form_template_version.group_permissions.allow_update

      if (this.item.is_closed === true) {
        // is closed so its locked
        return true;
      }

      // Locked if workflow engine locked or user's last state is completed
      if (this.item.is_locked || this.item.last_state_completed) {
        return true;
      }

      const current_user_groups = this.currentUser.groups;
      const userGroupIds = current_user_groups.map((group) => group.id);
      const form_template_version = this.item.form_template_version_instance;

      if (current_user_groups && form_template_version) {
        // Check if form_entry has creator permission to update
        const creatorPermission = form_template_version.group_permissions.find(
          (permission) => permission.group === -1
        );

        const isCurrentUserCreator =
          this.item.created_user === this.currentUser.id;

        if (creatorPermission && isCurrentUserCreator) {
          return !creatorPermission.allow_update;
        }

        // Check if form_entry has submitter permission to update
        const submitterPermission =
          form_template_version.group_permissions.find(
            (permission) => permission.group === -2
          );

        const isCurrentUserSubmitter =
          this.item.submitted_user === this.currentUser.id;

        if (submitterPermission && isCurrentUserSubmitter) {
          return !submitterPermission.allow_update;
        }

        const matchingPermissions =
          form_template_version.group_permissions.filter((permission) =>
            userGroupIds.includes(permission.group)
          );

        if (this.item.id) {
          // Existing Entry can they Update
          const hasUpdatePermission = matchingPermissions.some(
            (permission) => permission.allow_update === true
          );

          return !hasUpdatePermission || this.item.is_locked;
        } else {
          // New Entry can they Create
          const hasCreatePermission = matchingPermissions.some(
            (permission) => permission.allow_create === true
          );

          return !hasCreatePermission || this.item.is_locked;
        }
      }

      return this.item.is_locked;
    },
    isSubmitted() {
      return !!this.item.submitted_user;
    },
    canSubmit() {
      if (this.templateVersion.option_form_is_submitted) {
        if (this.last_validation !== true) {
          return false;
        }
        if (this.templateVersion.option_must_sign_off) {
          if (!this.item.sign_off_done) {
            return false; // they Must sign off First
          }
        }
        if (!this.isSubmitted) {
          return true; // not submitted but needs to be!
        }

        // Cannot change submit after 1 hour.
        if (moment().diff(this.item.submitted_date, "h") < 1) {
          return true;
        }
      }
      return false;
    },
    show_qr_code() {
      if (!this.item.uuid) {
        return false;
      }

      if (this.templateVersion) {
        return this.templateVersion.option_show_qr_code;
      }

      return false;
    },
    form_url() {
      var hostname = window.location.hostname;
      var port = window.location.port;
      var protocol = window.location.protocol;

      var url = protocol + "//" + hostname;
      if (
        port &&
        ((protocol === "http:" && port !== "80") ||
          (protocol === "https:" && port !== "443"))
      ) {
        url = url + ":" + port;
      }

      return url + "/forms247/entry/open/" + this.item.uuid;
    },
    lock_on_submit() {
      var form_template_version = _.filter(
        this.optionFormTemplateVersionItems,
        {
          id: this.item.form_template_version,
        }
      );

      if (
        this.item.submitted_date &&
        form_template_version &&
        form_template_version.length > 0
      ) {
        return form_template_version[0].option_lock_on_submit;
      }

      return false;
    },
    trigger_disabled_buttons() {
      return {};
    },
    formContext() {
      // Data to pass to the form fields that they may need to run.
      // states: name-keyed map of open states assigned to the current user.
      // workflowFields: server-side field visibility/disable rules for current state.
      var states = {};
      forEach(this.item.current_user_states, (state) => {
        states[state.state] = true;
      });

      if (!this.item.id) {
        // new id so on Start State
        states["start"]  = true;
      }
      return {
        states: states,
        isLocked: this.isLocked,
        isClosed: this.item.is_closed,
        workflowFields: this.workflowFields,
      };
    },
  },
  watch: {
    item: function () {
      if ("other_id" in this.$route.query) {
        for (var field_name in this.$route.query) {
          this.item.data[field_name] = this.$route.query[field_name];
        }

        // Set current date fields
        this.item.data["created_date"] = new Date();

        // Set current user fields
        if (this.currentUser && this.currentUser.descriptive_name) {
          this.item.data["current_user"] = this.currentUser.descriptive_name;
        }
      }

      // Object.keys(this.item.data).forEach((key) => {
      //   var keyValue = null;
      //   if (this.item.data && this.item.data[key]) {
      //     keyValue = this.item.data[key];
      //   }
      //   if (keyValue === null || keyValue.length === 0) {
      //     this.item.data[key] = [{}];
      //   }
      // });
    },
    currentState: function (newState) {
      // When workflow state changes, refresh field controls
      if (this.templateVersion && this.templateVersion.id) {
        this.GetFieldState(this.templateVersion.id, newState);
      }
    },
  },
  created() {
    if ("signOff" in this.$route.query) {
      this.theSignOff = parseInt(this.$route.query.signOff);
    }
  },
};
</script>
